import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import EliteMarkdownRenderer from './EliteMarkdown/EliteMarkdownRenderer';
import { RocketLaunchIcon, ChevronRightIcon, PlusIcon } from '@heroicons/react/24/solid';
import { LightBulbIcon, ArrowRightIcon, LockClosedIcon as LockIcon } from '@heroicons/react/24/outline';

import { useSubscription } from '../Context/SubscriptionContext';
import { useTenant } from '../Context/TenantContext';
import UpgradeScreen from './access/UpgradeScreen';

import API_BASE_URL from '../Config/config';
import { useNexusBilling } from '../NextGenBuilder/context/NexusBillingContext';
import { eliteAccessApi } from '../NextGenBuilder/api/nexusLearnerApi';
import { extractPurchaseRequired } from '../NextGenBuilder/api/billingApi';

/**
 * Elite Markdown Viewer
 * Enhanced version of MarkdownViewer with elite UX components
 *
 * TSG Nexus (`nexus={{ technology }}`): the markdown is NEVER read from the
 * static /docs bundle — a locked page simply is not served. The server
 * decides per page (free allowance / paid page unit / already unlocked) and
 * returns either the content or a structured 402.
 */
  const EliteMarkdownViewer = ({
  filePath,
  locked = false,
  docIndex: passedDocIndex,
  onHeadingsCollected,
  onContentLengthUpdate,
  readingProgress = 0,
  setReadingProgress = () => {},
  activeHeading = '',
  setActiveHeading = () => {},
  headings = [],
  // TSG Nexus only: `{ technology: '<slug>' }` switches content loading to the
  // gated server endpoint. Absent everywhere else, so the static fetch below
  // is untouched for the learner app and the college portals.
  nexus = null,
  // Called after a gated load so the parent can refresh the access map /
  // usage summary when a paid page unit was spent.
  onNexusAccess = null,
}) => {
  const [content, setContent] = useState('Loading...');
  const [metadata, setMetadata] = useState(null);
  const { theme } = useTheme();
  const { canAccessFeature, subscription, showPremiumPopup } = useSubscription();
  const sub = subscription || {};
  const { tenant } = useTenant();
  const isDark = theme === 'dark';
  const isTenantMode = !!tenant;
  const syncTimerRef = useRef(null);

  const nexusTechnology = nexus && nexus.technology ? nexus.technology : null;
  // Any `nexus` prop (even without a resolved technology yet) means the
  // static /docs path must never be read — the server is the only source.
  const nexusMode = !!nexus;
  const { catalog, openQuotaModal } = useNexusBilling();
  // Structured 402 payload for the page currently being read (Nexus only).
  const [nexusLocked, setNexusLocked] = useState(null);

  const eliteProduct = useMemo(() => {
    if (!catalog || !catalog.features) return null;
    const f = catalog.features.find((x) => x.code === 'ELITE_MASTERY');
    return f && f.products ? f.products[0] : null;
  }, [catalog]);

  // 🔒 Requirement for Elite Technology Mastery (Free User)
  // Logic is now centralized in SubscriptionContext and canAccessFeature
  const docIndex = passedDocIndex !== undefined
    ? passedDocIndex
    : 0;

  const technology = filePath ? filePath.split('/')[2] : '';
  // In TSG Nexus the backend is the only gate — the legacy "2 free previews"
  // rule does not apply to a builder buying pages per technology.
  const hasAccess = nexusMode ? true : canAccessFeature('ELITE_DOCS', { docIndex, technology });

  useEffect(() => {
    if (!hasAccess && !locked) {
        showPremiumPopup();
    }
  }, [hasAccess, locked, showPremiumPopup]);

  const isAccessBlocked = !hasAccess;

  useEffect(() => {
    const handleScroll = () => {
      const element = document.getElementById('mastery-content-area') || document.querySelector('.content-area') || document.querySelector('main');
      if (!element) return;
      
      const totalHeight = element.scrollHeight - element.clientHeight;
      const progress = totalHeight > 0 ? (element.scrollTop / totalHeight) * 100 : 0;
      setReadingProgress(progress);

  // --- NEW: Save progress dynamically ---
      if (filePath && progress > 5) {
        // Determine technology from filePath (e.g., /docs/java-premium/...)
        const pathParts = filePath.split('/');
        const tech = pathParts[2] || 'Elite Mastery'; 
        
        const lastReadData = {
          tech: tech,
          title: getTitleFromFilePath(filePath),
          progress: Math.round(progress),
          path: filePath,
          timestamp: new Date().toISOString()
        };
        localStorage.setItem('last_read_doc', JSON.stringify(lastReadData));

        // SYNC TO BACKEND (Throttled)
        const userId = localStorage.getItem('user_id') || localStorage.getItem('userId');
        if (userId) {
          if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
          syncTimerRef.current = setTimeout(() => {
            fetch(`${API_BASE_URL}/api/progress/reading`, {
              method: 'POST',
              headers: { 
                'Content-Type': 'application/json',
                'user-id': userId
              },
              body: JSON.stringify({
                module: 'Elite Mastery',
                technology: tech,
                file_path: filePath,
                scroll_position: Math.round(progress),
                reading_percentage: Math.round(progress),
                completion_percentage: Math.round(progress),
                status: progress > 90 ? 'Completed' : 'In Progress',
                time_spent: 5
              })
            }).catch(err => console.error('Failed to sync progress:', err));
          }, 5000);
        }

        // Mark as completed if > 90%
        if (progress > 90) {
          const completedKey = `completed_docs_${tech}`;
          const completed = JSON.parse(localStorage.getItem(completedKey) || "[]");
          if (!completed.includes(filePath)) {
            completed.push(filePath);
            localStorage.setItem(completedKey, JSON.stringify(completed));
          }
        }
        
        // Update global activity log
        const activity = JSON.parse(localStorage.getItem('user_activity') || "[]");
        const activityItem = {
          event: `Read ${lastReadData.title}`,
          tech: lastReadData.tech,
          time: new Date().toISOString(),
          type: 'reading'
        };
        // Keep only last 10 unique recent activities
        if (activity.length === 0 || activity[0].event !== activityItem.event) {
          const newActivity = [activityItem, ...activity.filter(a => a.event !== activityItem.event)].slice(0, 10);
          localStorage.setItem('user_activity', JSON.stringify(newActivity));
        }
      }

      // Simple active heading detection
      const headingElements = headings.map(h => document.getElementById(h.id)).filter(Boolean);
      let current = '';
      for (const el of headingElements) {
        const rect = el.getBoundingClientRect();
        if (rect.top < 200) {
          current = el.id;
        } else {
          break;
        }
      }
      setActiveHeading(current);
    };

    const scrollContainer = document.getElementById('mastery-content-area') || document.querySelector('.content-area') || document.querySelector('main');
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
    }
    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScroll);
      }
    };
  }, [headings, filePath]);

  const getTitleFromFilePath = (path) => {
    if (!path) return '';
    const raw = path.split('/').pop().replace('.md', '');
    return raw
      .replace(/[-_]/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  /**
   * Turn raw markdown into what the renderer shows. Extracted from the old
   * fetch().then() chain so the static /docs read and the TSG Nexus gated
   * read go through exactly the same cleanup, metadata extraction and title
   * fallback — one pipeline, two sources.
   */
  const applyRawText = useCallback((raw) => {
    let text = String(raw == null ? '' : raw);

    // Remove wrapper markdown code blocks (```markdown ... ```)
    text = text.trim();
    text = text.replace(/^```markdown\s*[\r\n]+/, '').replace(/[\r\n]+```\s*$/, '');
    text = text.replace(/^```\s*[\r\n]+/, '').replace(/[\r\n]+```\s*$/, '');
    text = text.trim();

    if (text.startsWith('```') && text.endsWith('```')) {
       const lines = text.split(/\r?\n/);
       if (lines.length > 2) {
          text = lines.slice(1, -1).join('\n').trim();
       }
    }

    if (onContentLengthUpdate) {
      onContentLengthUpdate(text.length);
    }

    // Extract metadata if present
    const metadataMatch = text.match(/^---\r?\n([\s\S]*?)\r?\n---/);
    if (metadataMatch) {
      const metaText = metadataMatch[1];
      const meta = {};
      metaText.split(/\r?\n/).forEach(line => {
        const [key, ...valueParts] = line.split(':');
        if (key && valueParts.length) {
          meta[key.trim()] = valueParts.join(':').trim();
        }
      });
      setMetadata(meta);
      text = text.replace(/^---\r?\n[\s\S]*?\r?\n---\r?\n?/, '');
    }

    const hasHeading = /^#\s.+/m.test(text);
    const title = getTitleFromFilePath(filePath);

    let headerPrefix = "";
    if (isTenantMode) {
      headerPrefix = `<div class="mb-6 inline-block bg-primary/10 border border-primary/30 px-4 py-1 rounded-full text-[10px] font-black uppercase tracking-widest text-primary">Institutional Content</div>\n\n`;
    }

    const finalContent = hasHeading ? (headerPrefix + text) : `${headerPrefix}# ${title}\n\n${text}`;
    setContent(finalContent.trim());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filePath, isTenantMode, onContentLengthUpdate]);

  useEffect(() => {
    if (!filePath || locked) return undefined;

    // --- NEW: Scroll to top when topic changes ---
    const scrollContainer = document.getElementById('mastery-content-area') || document.querySelector('.content-area') || document.querySelector('main');
    if (scrollContainer) {
      scrollContainer.scrollTop = 0;
    }

    let cancelled = false;

    // TSG Nexus: server-gated read. The static file is never touched, so a
    // locked page cannot be pulled out of /docs by anyone.
    if (nexusMode) {
      setNexusLocked(null);
      if (!nexusTechnology) {
        setContent('Select a technology from the Elite Mastery catalog to read this page.');
        return undefined;
      }
      setContent('Loading...');
      eliteAccessApi.getContent(nexusTechnology, filePath)
        .then((data) => {
          if (cancelled) return;
          applyRawText(data && data.content ? data.content : '');
          if (onNexusAccess) onNexusAccess(data);
        })
        .catch((err) => {
          if (cancelled) return;
          const payload = extractPurchaseRequired(err);
          if (payload) {
            setNexusLocked({ technology: nexusTechnology, ...payload });
            return;
          }
          setContent('❌ Content not found.');
        });
      return () => { cancelled = true; };
    }

    // Fix for potential relative path issues
    const cleanPath = filePath.startsWith('/') ? filePath : '/' + filePath;

    fetch(cleanPath)
      .then((res) => {
        if (!res.ok) throw new Error('File not found');
        return res.text();
      })
      .then((text) => {
        if (cancelled) return;
        applyRawText(text);
      })
      .catch(() => { if (!cancelled) setContent('❌ Content not found.'); });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filePath, locked, nexusMode, nexusTechnology]);

  // TSG Nexus: this exact page needs a purchase. Same locked shell as the
  // legacy overlay, but the copy and the price come from the server payload
  // and the catalog — nothing is hardcoded.
  if (nexusLocked) {
    const units = eliteProduct ? eliteProduct.unitsPerPurchase : null;
    return (
      <div className="flex items-center justify-center min-h-[70vh] text-center">
        <div className={`
          px-8 py-10
          rounded-2xl
          shadow-xl
          space-y-6
          border
          max-w-md
          ${isDark ? 'bg-gray-900 border-primary/30 text-white' : 'bg-white border-primary/30 text-gray-900'}
        `}>
          <div className="text-6xl">🔒</div>
          <h2 className="text-2xl font-bold">This page needs unlocking</h2>
          <p className="text-base opacity-80">
            {nexusLocked.message
              || "You've read all your free pages for this technology. Unlocked pages stay readable forever."}
          </p>
          <button
            type="button"
            onClick={() => openQuotaModal(nexusLocked)}
            className="inline-block px-8 py-3 bg-primary text-white rounded-xl font-semibold transition-all hover:bg-primary/90 shadow-lg shadow-primary/20"
          >
            {units ? `Unlock ${units} pages` : 'Unlock more pages'}
          </button>
        </div>
      </div>
    );
  }

  if (locked) {
    return (
      <div className="flex items-center justify-center min-h-[70vh] text-center">
        <div className={`
          px-8 py-10
          rounded-2xl
          shadow-xl
          space-y-6
          border
          max-w-md
          ${isDark ? 'bg-gray-900 border-primary/30 text-white' : 'bg-white border-primary/30 text-gray-900'}
        `}>
          <div className="text-6xl">🔒</div>
          <h2 className="text-2xl font-bold">Premium Content Locked</h2>
          <p className="text-base opacity-80">
            Unlock unlimited access to all premium engineering content
          </p>
          <div className="bg-primary/10 p-4 rounded-lg border border-primary/20">
            <div className="text-3xl font-bold text-primary">₹2999</div>
            <div className="text-sm opacity-70">Starting Price • Full Access</div>
          </div>
          <a
            href="/skillgenpayments"
            className="
              inline-block
              px-8
              py-3
              bg-primary
              text-white
              rounded-xl
              font-semibold
              transition-all
              hover:bg-primary/90
              shadow-lg
              shadow-primary/20
            "
          >
            🔓 Unlock All Content Now
          </a>
        </div>
      </div>
    );
  }
  if (isAccessBlocked) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-white/5">
        <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mb-6">
          <LockIcon size={40} className="text-primary" />
        </div>
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Preview Limit Reached</h2>
        <p className="text-gray-600 dark:text-gray-400 max-w-md mb-8">
          You've viewed your 2 free preview documents. Please upgrade to a premium plan to unlock full access to all Elite Technology Mastery content.
        </p>
        <button
          onClick={() => window.location.href = "/skillgenpayments"}
          className="bg-primary hover:bg-primary/90 text-white font-bold py-3 px-8 rounded-xl transition-all hover:scale-105 active:scale-95 shadow-lg shadow-primary/20"
        >
          Upgrade to Premium
        </button>
      </div>
    );
  }

  return (
    <div 
      className="elite-content-area select-none" 
      id="markdown-content"
      onContextMenu={(e) => e.preventDefault()}
      onCopy={(e) => e.preventDefault()}
      onPaste={(e) => e.preventDefault()}
      onCut={(e) => e.preventDefault()}
    >
      <EliteMarkdownRenderer content={content} onHeadingsCollected={onHeadingsCollected} />
    </div>
  );
};

export default EliteMarkdownViewer;

